import dayjs from 'dayjs';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { User } from '@/modules/user/models/User';
import { Estate } from '@/modules/estate/models/Estate';
import { Message } from '@/modules/messenger/_modules/message/models/Message';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { ConversationType } from '@/modules/messenger/_modules/conversation/constants/ConversationType';
import Lang from '@/modules/core/app/services/Lang';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { ClearBotUser } from '@/modules/user/constants/ClearBotUser';
import { AuthUser } from '@/modules/auth/models/AuthUser';
import { ReadReceipt } from '@/modules/readreceipt/models/ReadReceipt';
import { MessageMention } from '@/modules/messenger/_modules/message/models/MessageMention';
import { ConversationUser } from '@/modules/messenger/_modules/conversation/models/ConversationUser';
import { ConversationSubType } from '@/modules/messenger/_modules/conversation/constants/ConversationSubType';

export class Conversation extends BaseEntityModel {
  id?: number;
  estateId?: number;
  partnerEstateId?: number;
  name?: string;
  type?: ConversationType;
  subType?: ConversationSubType;
  isArchived?: boolean;
  isClaraDrafting?: boolean;
  createdByUserId?: number;
  conversationUsers?: Relationship<ConversationUser[] | null>;
  estate?: Relationship<Estate | null>;
  users?: Relationship<User[]>;
  messages?: Relationship<Message[]>;
  createdByUser?: Relationship<User | null>;

  constructor(model: Partial<Conversation> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.partnerEstateId = model.partnerEstateId;
    this.name = model.name;
    this.type = model.type;
    this.subType = model.subType;
    this.isArchived = model.isArchived;
    this.isClaraDrafting = model.isClaraDrafting;
    this.createdByUserId = model.createdByUserId;

    // @ts-ignore
    this.conversationUsers = ConversationUser.mapModelsOrNull(model.conversationUsers);
    // @ts-ignore
    this.estate = Estate.mapModelOrNull(model.estate);
    // @todo: sachin please convert the following to UserCollection and MessageCollection
    this.users = User.mapModels(model.users);
    this.messages = Message.mapModels(model.messages);
    // @ts-ignore
    this.createdByUser = User.mapModelOrNull(model.createdByUser);
  }

  getModelName(): ModelType {
    return ModelType.CONVERSATION;
  }

  hasUser(userId: number): boolean {
    return !!this.participants?.some((user: User) => user.id === userId);
  }

  hasMessage(messageId: number): boolean {
    return !!this.messages?.some((message: Partial<Message>) => message.id === messageId);
  }

  addUser(user: User): void {
    if (!this.conversationUsers) {
      this.conversationUsers = [];
    }

    this.conversationUsers?.push(
      ConversationUser.mapModel({ conversationId: this.id, userId: user.id, user: user })
    );
  }

  addMessage(message: Message): void {
    if (message.userId && message.user && !this.hasUser(message.userId as number)) {
      this.addUser(message.user as User);
    }

    this.messages?.push(message);
  }

  updateMessageId(key: string, id: number): void {
    const message: Message | undefined = this.getMessageByKey(key);
    if (!message) {
      return;
    }

    message.id = id;
  }

  updateIsDraft(key: string, isDraft: boolean): void {
    const message: Message | undefined = this.getMessageByKey(key);
    if (!message) {
      return;
    }

    message.isDraft = isDraft;
  }

  updateMessageMentions(key: string, messageMentions: MessageMention[] | undefined): void {
    const message: Message | undefined = this.getMessageByKey(key);
    if (!message) {
      return;
    }

    message.messageMentions = MessageMention.mapModelsOrNull(messageMentions) ?? undefined;
  }

  updateReadReceipts(key: string, readReceipts: ReadReceipt[] | undefined): void {
    const message: Message | undefined = this.getMessageByKey(key);
    if (!message) {
      return;
    }

    // @ts-ignore
    message.readReceipts = ReadReceipt.mapModelsOrNull(readReceipts);
  }

  updateUpdatedAt(key: string, updatedAt: string): void {
    const message: Message | undefined = this.getMessageByKey(key);
    if (!message) {
      return;
    }

    message.updatedAt = updatedAt;
  }

  setMessageReceiptsAsRead(userId: number): void {
    this.messages?.forEach((message: Message) =>
      message.readReceipts?.forEach((receipt: ReadReceipt) => {
        if (receipt.userId === userId) {
          receipt.readAt = dayjs().format(DateFormat.ISO_TIME_WITH_TIMEZONE);
        }
      })
    );
  }

  isUnread(userId: number): boolean {
    return !!this.messages?.some(
      (message: Message) =>
        !!message.readReceipts?.some(
          (receipt: ReadReceipt) => receipt.userId === userId && !receipt.readAt
        )
    );
  }

  removeMessage(messageId: number): void {
    if (!this.hasMessage(messageId)) {
      return;
    }

    const index: number | undefined = this.messages?.findIndex(
      (message: Partial<Message>): boolean => message.id === messageId
    );

    this.messages?.splice(index as number, 1);
  }

  setName(name: string): void {
    this.name = name;
  }

  setSubtype(subType: ConversationSubType): void {
    this.subType = subType;
  }

  /**
   * Participants who can can send messages in a conversation.
   * Users who are not deleted and users who are not removed from a conversation
   */
  get activeParticipants(): User[] {
    return (
      this.conversationUsers
        ?.filter(
          (conversationUser: ConversationUser) =>
            // @ts-ignore
            !conversationUser.deletedAt && !conversationUser.user?.estateAccount?.deletedAt
        )
        .map((conversationUser: ConversationUser) => conversationUser.user as User) ?? []
    );
  }

  /**
   * Participants where the conversation user is deleted
   */
  get inactiveParticipants(): User[] {
    return (
      this.conversationUsers
        ?.filter((conversationUser: ConversationUser) => !!conversationUser.user?.deletedAt)
        .map((conversationUser: ConversationUser) => conversationUser.user as User) ?? []
    );
  }

  /**
   * Participants where the conversation user is not deleted and there is a user
   */
  get visibleParticipants(): User[] {
    return (
      this.conversationUsers
        ?.filter(
          (conversationUser: ConversationUser) =>
            !conversationUser.deletedAt && !!conversationUser.user
        )
        .map((conversationUser: ConversationUser) => conversationUser.user as User) ?? []
    );
  }

  /**
   * Participants are users where conversation user is present
   */
  get participants(): User[] {
    return (
      this.conversationUsers
        ?.filter((conversationUser: ConversationUser) => !!conversationUser.user)
        .map((conversationUser: ConversationUser) => conversationUser.user as User) ?? []
    );
  }

  get title(): string {
    if (this.name) {
      return this.isGeneral ? Lang.t('inbox.general-chat') : this.name;
    }

    return this.visibleParticipants && this.visibleParticipants.length > 2
      ? Lang.t('inbox.group-chat')
      : Lang.t('inbox.no-conversation-name');
  }

  get firstMessage(): Message | null {
    if (!this.messages) {
      return null;
    }

    return this.messages[0];
  }

  get lastMessage(): Message | null {
    if (!this.messages) {
      return null;
    }

    const nonSystemMessages: Message[] = this.messages.filter(
      (message: Message) => !message.isSystemMessage
    );

    return nonSystemMessages[nonSystemMessages.length - 1];
  }

  get lastMessageText(): string {
    const message: Message | null = this.lastMessage;
    if (!message) {
      return '';
    }

    const user: User | undefined = this.participants?.find(
      (user: User) => user.id === message?.userId
    );
    const firstName: string = user?.contact?.firstName ?? '';
    const messagePrefix: string = message.sentiment
      ? `${message.sentiment} ${firstName}`
      : firstName;

    return messagePrefix !== ''
      ? `<section class="is-flex"><div>${messagePrefix}: </div><div class="panel-message">${message.text}</div></section>`
      : `${message.text}`;
  }

  get lastGeneralMessageText(): string {
    const message: Message | null = this.lastMessage;
    if (!message) {
      return '';
    }

    return `<div class="panel-message">${message.text}</div>`;
  }

  get lastMessageTimeAgo(): string | undefined {
    return this.lastMessage?.timeAgoUpdatedAt;
  }

  get isDirect(): boolean {
    return this.visibleParticipants ? this.visibleParticipants.length <= 2 : false;
  }

  get isGeneral(): boolean {
    return this.subType === ConversationSubType.GENERAL;
  }

  get isVisible(): boolean {
    return !!(this.messages?.length || this.isBot || this.isGeneral);
  }

  get isBot(): boolean {
    return this.subType === ConversationSubType.BOT;
  }

  get canMessage(): boolean {
    return !(
      this.isBot ||
      // hard-deleted user
      this.visibleParticipants?.length === 1 ||
      // soft-deleted user
      (this.isDirect && this.hasDeletedUser)
    );
  }

  get hasDeletedUser(): boolean {
    return !!this.visibleParticipants?.some(
      (user: User) => !!user.deletedAt || !!user.estateAccount?.deletedAt
    );
  }

  getSearchValue(authUser: AuthUser): string | undefined {
    if (this.isBot) {
      return ClearBotUser.contact?.firstName;
    }

    if (this.isDirect) {
      return (
        this.participants?.find((user: User) => user.id !== authUser.id)?.contact?.firstName ?? ''
      );
    }

    return this.title ?? '';
  }

  get draftMessage(): Message | undefined {
    return this.messages?.find((message: Message) => message.isDraft && !message.hasClaraDraft);
  }

  get claraDraftMessage(): Message | undefined {
    return this.messages?.find((message: Message) => message.hasClaraDraft);
  }

  get completedMessages(): Message[] {
    return (
      this.messages
        ?.filter((message: Message) => !message.isDraft)
        .sort((a: Message, b: Message) => {
          if (!a.updatedAt && !b.updatedAt) {
            return 0;
          }
          if (!a.updatedAt && b.updatedAt) {
            return 1;
          }
          if (a.updatedAt && !b.updatedAt) {
            return -1;
          }

          return dayjs(a.updatedAt).diff(dayjs(b.updatedAt));
        }) ?? []
    );
  }

  get hasDraftMessage(): boolean {
    return !!this.draftMessage || !!this.claraDraftMessage;
  }

  get outOfOfficeParticipants(): User[] {
    return this.visibleParticipants.filter((user: User) => user.isOutOfOffice);
  }

  archive(): void {
    this.isArchived = true;
  }

  unarchive(): void {
    this.isArchived = false;
  }

  lastUnreadMessage(authUser: AuthUser): Message | null {
    if (!this.messages) {
      return null;
    }

    const message: Message | undefined = this.messages.find((message: Message) => {
      return message.readReceipts?.find(
        (readReceipt: ReadReceipt) => !readReceipt.readAt && readReceipt.userId === authUser.id
      );
    });

    return message || null;
  }

  private getMessageByKey(key: string): Message | undefined {
    return this.messages?.find((message: Message) => message.key === key);
  }

  isRelatedToEstate(estateId: number): boolean {
    return (
      this.estateId === estateId ||
      (this.partnerEstateId === estateId && this.subType === ConversationSubType.GENERAL)
    );
  }
}
